import dayjs from 'dayjs';
import {
  RM_STEPPER_STAGES,
  RmActor,
  RmAdvanceStage,
  RmCorrectionStage,
  RmFileStatus,
  RmFileUnavailableReason,
  RmHistoryEntry,
  RmJobCounts,
  RmJobDetail,
  RmJobFormat,
  RmJobSource,
  RmPersonalizationLabel,
  RmPrintJobRow,
  RmPrintStage,
  RmQueueOrder,
} from './types';
import { formatMessage, rmQueueStrings } from './strings';

const {
  detail,
  stageLabels,
  personalization,
  sourceLabels,
  formatLabels,
  customizationLabels,
  refusals,
} = rmQueueStrings;

/** A queue tab: one lane, or every lane but cancelled. */
export type RmTabKey = RmPrintStage | 'all';

export const stageLabel = (stage: RmPrintStage): string =>
  stageLabels[stage] ?? stage;

/** The issue's 'SEP' + '2026' rendered for a human. */
export const issueLabel = (month: string, year: string): string => {
  const titled = month
    ? month.charAt(0).toUpperCase() + month.slice(1).toLowerCase()
    : month;
  return `${titled} ${year}`.trim();
};

/** How a personalisation state renders: label, tooltip, and badge colour. */
export type PersonalizationDisplay = {
  text: string;
  hint: string;
  color: 'success' | 'gray' | 'warning';
};

/**
 * Maps the three-state label to its display. `unknown` is deliberately a
 * distinct treatment (its own word and a warning colour), not folded into
 * "same as last month" — the whole point of the third state is that we do not
 * know, and pretending otherwise manufactures a fact.
 */
export const personalizationDisplay = (
  label: RmPersonalizationLabel,
): PersonalizationDisplay => {
  switch (label) {
    case 'personalised':
      return {
        text: personalization.personalized,
        hint: personalization.personalizedHint,
        color: 'success',
      };
    case 'carried_forward':
      return {
        text: personalization.carriedForward,
        hint: personalization.carriedForwardHint,
        color: 'gray',
      };
    case 'unknown':
    default:
      // An unrecognised value from the server is exactly the "we do not know"
      // case — it must fall to `unknown`, never manufacture "carried forward".
      return {
        text: personalization.unknown,
        hint: personalization.unknownHint,
        color: 'warning',
      };
  }
};

/** Who or what filed the job, as chip text. `null` predates the field. */
export const sourceLabel = (source: RmJobSource | null | undefined): string =>
  (source && sourceLabels[source]) || sourceLabels.unknown;

/** One chip colour per source, so a lane reads at a glance. */
export const sourceChipClass = (
  source: RmJobSource | null | undefined,
): string => {
  switch (source) {
    case 'member':
      return 'bg-blue-50 text-blue-800';
    case 'operator':
      return 'bg-pink-50 text-pink-800';
    case 'monthly_batch':
      return 'bg-purple-50 text-purple-800';
    case 'hubspot_import':
      return 'bg-neutral-100 text-neutral-700';
    default:
      return 'bg-neutral-50 text-neutral-500';
  }
};

export const formatLabel = (format: RmJobFormat | null | undefined): string =>
  (format && formatLabels[format]) || formatLabels.unknown;

/** What the customization cell says, and why when it is a personalisation state. */
export type CustomizationDisplay = { text: string; hint?: string };

/**
 * One plain label for how this issue's content was made. A setup-team or
 * default design says so; a DMP job (or one that does not say) falls back to
 * the member's personalisation, which is the fact that matters there. A value
 * this screen does not know yet is shown as written rather than hidden.
 */
export const customizationDisplay = (
  job: Pick<RmPrintJobRow, 'customization' | 'personalizationLabel'>,
): CustomizationDisplay => {
  const value = job.customization;
  if (value && customizationLabels[value]) {
    return { text: customizationLabels[value] };
  }
  if (value && value !== 'dmp') {
    const words = value.replace(/_/g, ' ');
    return { text: words.charAt(0).toUpperCase() + words.slice(1) };
  }
  const person = personalizationDisplay(job.personalizationLabel);
  return { text: person.text, hint: person.hint };
};

/** The date column's header, one per tab. */
export type RmTimeColumn =
  | 'inSetupFor'
  | 'waitingSinceSent'
  | 'waitingToShip'
  | 'shippedOn'
  | 'cancelledOn'
  | 'lastMoved';

/**
 * What the date cell leads with: the wait for work in hand, the day for
 * finished work, and the day plus the stage in "All" (mixed lanes).
 */
export type RmDateCell = 'age' | 'date' | 'stage';

/** How a lane's table is laid out. */
export interface LaneLayout {
  /** Only "All" mixes stages, so only "All" needs the Stage column. */
  showStage: boolean;
  /** The batch move this lane offers, or null (no checkboxes then). */
  bulkTarget: RmAdvanceStage | null;
  /** What the date column is called in this lane. */
  timeColumn: RmTimeColumn;
  dateCell: RmDateCell;
  /** The order DS applies when the operator has not flipped it. */
  defaultOrder: RmQueueOrder;
}

/** The one batch move out of a lane: Sent to print → Printed → Shipped. */
export const bulkTargetForLane = (tab: RmTabKey): RmAdvanceStage | null => {
  if (tab === 'sent_to_print') return 'printed';
  if (tab === 'printed') return 'shipped';
  return null;
};

/**
 * Every lane sorts by the date its jobs entered it (DS `stageEnteredAt`). Work
 * in hand comes oldest first, finished work and "All" newest first, the same
 * defaults DS applies when no order is sent.
 */
const DATE_COLUMN_BY_TAB: Record<
  RmTabKey,
  Pick<LaneLayout, 'timeColumn' | 'dateCell' | 'defaultOrder'>
> = {
  in_setup: { timeColumn: 'inSetupFor', dateCell: 'age', defaultOrder: 'oldest' },
  sent_to_print: { timeColumn: 'waitingSinceSent', dateCell: 'age', defaultOrder: 'oldest' },
  printed: { timeColumn: 'waitingToShip', dateCell: 'age', defaultOrder: 'oldest' },
  shipped: { timeColumn: 'shippedOn', dateCell: 'date', defaultOrder: 'newest' },
  cancelled: { timeColumn: 'cancelledOn', dateCell: 'date', defaultOrder: 'newest' },
  all: { timeColumn: 'lastMoved', dateCell: 'stage', defaultOrder: 'newest' },
};

export const laneLayout = (tab: RmTabKey): LaneLayout => ({
  showStage: tab === 'all',
  bulkTarget: bulkTargetForLane(tab),
  ...(DATE_COLUMN_BY_TAB[tab] ?? DATE_COLUMN_BY_TAB.all),
});

/** The other order, for a click on the date header. */
export const flipOrder = (order: RmQueueOrder): RmQueueOrder =>
  order === 'oldest' ? 'newest' : 'oldest';

/** "1 day" / "3 days"; an unknown age is a dash, never a confident zero. */
export const ageText = (days: number | null): string => {
  if (days === null || days === undefined) {
    return rmQueueStrings.queue.ageUnknown;
  }
  return formatMessage(
    days === 1 ? rmQueueStrings.queue.day : rmQueueStrings.queue.days,
    { days },
  );
};

/** `Oct 28, 2026`, or a dash. */
export const formatDate = (iso?: string): string =>
  iso ? dayjs(iso).format('MMM D, YYYY') : '—';

/** "today" / "1 day ago" / "15 days ago"; nothing when the age is unknown. */
export const agoText = (days: number | null | undefined): string | undefined => {
  if (days === null || days === undefined) {
    return undefined;
  }
  if (days === 0) {
    return rmQueueStrings.queue.today;
  }
  return formatMessage(
    days === 1 ? rmQueueStrings.queue.dayAgo : rmQueueStrings.queue.daysAgo,
    { days },
  );
};

const DAY_MS = 24 * 60 * 60 * 1000;

const utcDayIndex = (date: Date): number =>
  Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()) / DAY_MS;

/**
 * Whole UTC calendar days from `iso` to `now`, so the count always agrees with
 * the day written above it ("Oct 30" last evening is "1 day ago", not "today",
 * as DS's whole-24h `stageAgeDays` would say). `null` for an unreadable date.
 */
export const calendarDaysSince = (iso: string, now: Date): number | null => {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) {
    return null;
  }
  return Math.max(0, utcDayIndex(now) - utcDayIndex(at));
};

/** The job's fields the date cell reads; the stage ones are absent on an older DS. */
export type DateCellJob = Pick<RmPrintJobRow, 'stage'> &
  Partial<
    Pick<RmPrintJobRow, 'stageEnteredAt' | 'stageAgeDays' | 'filedAt' | 'ageDays'>
  >;

/**
 * The date and day count the cell shows. An older DS sends no `stageAgeDays`
 * at all, and then the filing date stands in, as the cell did before. A DS
 * that sends `stageAgeDays: null` is saying "unknown", and that is kept.
 */
const cellStamp = (
  job: DateCellJob,
  now: Date,
): { at?: string; days: number | null } => {
  const legacy = job.stageAgeDays === undefined && !job.stageEnteredAt;
  const at = legacy ? job.filedAt : job.stageEnteredAt;
  if (at) {
    return { at, days: calendarDaysSince(at, now) };
  }
  return { days: (legacy ? job.ageDays : job.stageAgeDays) ?? null };
};

/**
 * The date cell's two lines. A wait reads "20 days" over "since Oct 11"; a
 * finished job "Oct 16, 2026" over "15 days ago"; a row of "All" the day it
 * last moved over the stage it moved to. An unknown date is a dash alone.
 * Days are UTC calendar days, the same clock as the window note.
 */
export const dateCellLines = (
  job: DateCellJob,
  cell: RmDateCell,
  now: Date = new Date(),
): { main: string; sub?: string } => {
  const { at, days } = cellStamp(job, now);
  if (cell === 'age') {
    return {
      main: ageText(days),
      sub: at
        ? formatMessage(rmQueueStrings.queue.since, {
            date: utcDay(new Date(at), { month: 'short', day: 'numeric' }),
          })
        : undefined,
    };
  }
  if (!at) {
    return { main: rmQueueStrings.queue.ageUnknown };
  }
  return {
    main: utcDay(new Date(at), { month: 'short', day: 'numeric', year: 'numeric' }),
    sub: cell === 'stage' ? stageLabel(job.stage) : agoText(days),
  };
};

const utcDay = (date: Date, options: Intl.DateTimeFormatOptions): string =>
  date.toLocaleDateString('en-US', { ...options, timeZone: 'UTC' });

/**
 * The page subtitle: the issue whose window last closed and the issue still
 * open, e.g. "Nov 2026 issue: window closed Oct 22 · Dec 2026 issue: open
 * until Nov 22". Same rule as DS `isIssueWindowClosed`: an issue's window
 * closes at the end of the cutoff day (UTC) of the month before it.
 */
export const issueWindowNote = (now: Date, cutoffDay: number): string => {
  const cutoffOf = (issue: Date): Date =>
    new Date(
      Date.UTC(issue.getUTCFullYear(), issue.getUTCMonth() - 1, cutoffDay + 1),
    );
  const lastOpenDay = (issue: Date): Date =>
    new Date(cutoffOf(issue).getTime() - 24 * 60 * 60 * 1000);

  // Next month's issue closes this month; once that has passed, the one after.
  let open = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
  if (now.getTime() >= cutoffOf(open).getTime()) {
    open = new Date(Date.UTC(open.getUTCFullYear(), open.getUTCMonth() + 1, 1));
  }
  const closed = new Date(Date.UTC(open.getUTCFullYear(), open.getUTCMonth() - 1, 1));
  const issue = { month: 'short', year: 'numeric' } as const;
  const day = { month: 'short', day: 'numeric' } as const;

  return formatMessage(rmQueueStrings.queue.windowNote, {
    closedIssue: utcDay(closed, issue),
    closedOn: utcDay(lastOpenDay(closed), day),
    openIssue: utcDay(open, issue),
    openUntil: utcDay(lastOpenDay(open), day),
  });
};

/** The lanes an empty Sent to print points at, in lane order. */
const HINT_LANES: RmPrintStage[] = ['in_setup', 'printed', 'shipped'];

export interface RmLaneCount {
  lane: RmPrintStage;
  count: number;
}

/**
 * The hint on an empty Sent to print lane when other lanes hold jobs, so an
 * operator landing on the default tab learns where the work is: every
 * non-empty lane, in lane order. Printed is one of them because after the
 * month's batch "Mark as printed" the whole issue sits there. Null otherwise.
 */
export const emptyLaneHint = (
  tab: RmTabKey,
  counts: RmJobCounts | undefined,
): RmLaneCount[] | null => {
  if (tab !== 'sent_to_print' || !counts || counts.sent_to_print !== 0) {
    return null;
  }
  const lanes = HINT_LANES.map((lane) => ({ lane, count: counts[lane] ?? 0 }))
    .filter(({ count }) => count > 0);
  return lanes.length > 0 ? lanes : null;
};

/** "Nothing waiting to print. Printed has 48, Shipped has 48." */
export const emptyLaneHintText = (lanes: RmLaneCount[]): string =>
  formatMessage(rmQueueStrings.queue.emptyLaneHint, {
    lanes: lanes
      .map(({ lane, count }) =>
        formatMessage(rmQueueStrings.queue.laneHas, {
          lane: stageLabel(lane),
          count,
        }),
      )
      .join(', '),
  });

/** The batch confirm's "who moves": up to three names, then a count. */
export const confirmNames = (
  names: string[],
): { names: string; more: number } => {
  const shown = names.slice(0, 3);
  return { names: shown.join(', '), more: names.length - shown.length };
};

/**
 * How the file's unavailability reads. Each reason is distinct wording, because
 * "not rendered yet" (it will exist) and "never generated" (it will not) mean
 * different things to an operator deciding whether to wait or escalate.
 */
export const fileUnavailableWording = (
  reason?: RmFileUnavailableReason,
): string => {
  switch (reason) {
    case 'not-rendered-yet':
      return detail.files.notRenderedYet;
    case 'not-generated':
      return detail.files.notGenerated;
    case 'lookup-failed':
      return detail.files.lookupFailed;
    default:
      return detail.files.unavailable;
  }
};

export const fileKindLabel = (kind: RmFileStatus['kind']): string =>
  kind === 'magazine' ? detail.files.magazine : detail.files.covers;

/** 18_400_000 → "17.5 MB". */
export const formatFileSize = (bytes?: number): string => {
  if (bytes === undefined || bytes === null || Number.isNaN(bytes)) {
    return '—';
  }
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** `Oct 28, 2026 2:14 PM`, or a dash. */
export const formatDateTime = (iso?: string): string =>
  iso ? dayjs(iso).format('MMM D, YYYY h:mm A') : '—';

/** Same bound DS applies to the magazine upload. */
export const RM_MAGAZINE_MAX_BYTES = 100 * 1024 * 1024;

/** The check before an upload, so an obvious refusal costs no round trip. */
export const magazineClientError = (
  file: Pick<File, 'name' | 'type' | 'size'>,
): string | null => {
  const isPdf =
    file.type === 'application/pdf' ||
    (file.type === '' && file.name.toLowerCase().endsWith('.pdf'));
  if (!isPdf) {
    return detail.files.notPdf;
  }
  if (file.size > RM_MAGAZINE_MAX_BYTES) {
    return detail.files.tooLarge;
  }
  return null;
};

/** DS's bounds on a cancel reason, applied to the trimmed text. */
export const CANCEL_REASON_MIN_LENGTH = 3;
export const CANCEL_REASON_MAX_LENGTH = 500;

/** DS's bound on a correction reason (trimmed, 1–500). */
export const CORRECTION_REASON_MAX_LENGTH = 500;

export const validateCancelReason = (
  reason: string,
): 'too_short' | 'too_long' | null => {
  const length = reason.trim().length;
  if (length < CANCEL_REASON_MIN_LENGTH) return 'too_short';
  if (length > CANCEL_REASON_MAX_LENGTH) return 'too_long';
  return null;
};

/** The `code` of a DS refusal, from the error envelope. Never `error`. */
export const refusalCode = (error: unknown): string | undefined => {
  const code = (error as { response?: { data?: { code?: unknown } } })?.response
    ?.data?.code;
  return typeof code === 'string' ? code : undefined;
};

/** ABO's text for a refusal code it knows, else the fallback. */
export const refusalMessage = (code: string | undefined, fallback: string): string =>
  (code && refusals[code]) || fallback;

/** ABO's text for a failed request: its refusal code's, else the fallback. */
export const errorMessage = (error: unknown, fallback: string): string =>
  refusalMessage(refusalCode(error), fallback);

/**
 * ABO's text for a refused operator send to print (`create-ticket`). DS checks
 * the render first (superseded, preview, not done, no PDF) and answers with
 * its own code before any write.
 */
export const sendToPrintErrorMessage = (error: unknown): string =>
  errorMessage(error, rmQueueStrings.sendToPrint.failed);

/**
 * A too-broad member search is a 400 from the server, not a client error to
 * swallow. This tells the two apart so the queue can show the "narrow it down"
 * hint only for that case.
 */
export const isSearchTooBroadError = (error: unknown): boolean => {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  return status === 400;
};

/** The one thing the hero offers, resolved from the server's `actions`. */
export type HeroAction =
  | { kind: 'ready' }
  | { kind: 'readyBlocked' }
  | { kind: 'advance'; to: RmAdvanceStage }
  | { kind: 'terminal'; text: string };

export const heroAction = (job: RmJobDetail): HeroAction => {
  if (job.stage === 'in_setup') {
    return job.actions?.markReady ? { kind: 'ready' } : { kind: 'readyBlocked' };
  }
  const next = job.actions?.nextStage ?? job.allowedNextStage;
  if (next) {
    return { kind: 'advance', to: next };
  }
  return {
    kind: 'terminal',
    text:
      job.stage === 'cancelled'
        ? detail.hero.terminalCancelled
        : detail.hero.terminalShipped,
  };
};

/** The stages a correction may move back to, from the server. */
export const correctionStagesOf = (job: RmJobDetail): RmCorrectionStage[] =>
  job.actions?.correctionStages ?? job.allowedCorrectionStages ?? [];

export type StepState = 'done' | 'current' | 'todo' | 'cancelled';

/** The stage a cancelled job left, from its `cancelled` history entry. */
export const cancelledFromStage = (
  job: Pick<RmJobDetail, 'history'>,
): RmPrintStage | undefined =>
  job.history.find((entry) => entry.meta?.type === 'cancelled')?.from;

/**
 * The stepper In setup → Sent to print → Printed → Shipped. A cancelled job
 * stops at the stage it was cancelled from, drawn as cancelled.
 */
export const stepStates = (
  job: Pick<RmJobDetail, 'stage' | 'history'>,
): Array<{ step: RmCorrectionStage; state: StepState }> => {
  const cancelled = job.stage === 'cancelled';
  const anchor = cancelled ? cancelledFromStage(job) : job.stage;
  const anchorIndex = RM_STEPPER_STAGES.indexOf(anchor as RmCorrectionStage);
  return RM_STEPPER_STAGES.map((step, index) => {
    let state: StepState = 'todo';
    if (anchorIndex === -1) {
      state = 'todo';
    } else if (index < anchorIndex) {
      state = 'done';
    } else if (index === anchorIndex) {
      // A shipped job has finished: its last step is done, not in progress.
      state = cancelled ? 'cancelled' : step === 'shipped' ? 'done' : 'current';
    }
    return { step, state };
  });
};

/**
 * History in true chronological order (oldest first). DS sends it sorted
 * newest first (createdAt, then _id), so a reverse is exact, including for
 * entries that share a timestamp. Never mutates the cached array.
 */
export const historyChronological = (
  history: RmHistoryEntry[],
): RmHistoryEntry[] => [...history].reverse();

/** The history chip: who did it. An operator is named; the rest are kinds. */
export const actorLabel = (actor?: RmActor): string => {
  if (!actor) return detail.history.actorSystem;
  if (actor.type === 'system') return detail.history.actorSystem;
  if (actor.type === 'member') return detail.history.actorMember;
  return actor.id || sourceLabels.operator;
};

export const actorChipClass = (actor?: RmActor): string => {
  switch (actor?.type) {
    case 'member':
      return 'bg-blue-50 text-blue-800';
    case 'operator':
      return 'bg-pink-50 text-pink-800';
    default:
      return 'bg-neutral-100 text-neutral-700';
  }
};

const metaString = (entry: RmHistoryEntry, key: string): string | undefined => {
  const value = entry.meta?.[key];
  return typeof value === 'string' && value.trim() !== '' ? value : undefined;
};

/** What happened, in plain words, by the entry's `meta.type`. */
export const historySummary = (entry: RmHistoryEntry): string => {
  const to = stageLabel(entry.to);
  const from = entry.from ? stageLabel(entry.from) : '';
  switch (entry.meta?.type) {
    case 'stage_corrected':
      return formatMessage(detail.history.corrected, { from, to });
    case 'cancelled':
      return detail.history.cancelled;
    case 'magazine_uploaded':
      return metaString(entry, 'replacedKey')
        ? detail.history.magazineReplaced
        : detail.history.magazineUploaded;
    case 'filing_upgraded':
      return formatMessage(detail.history.filingUpgraded, { from, to });
    case 'filing_merged':
      return detail.history.filingMerged;
    case 'imported':
      return formatMessage(detail.history.imported, { to });
    case 'import_merged':
      return detail.history.importMerged;
    case 'import_reverted':
      return detail.history.importReverted;
    default:
      if (!entry.from) {
        return formatMessage(detail.history.created, { to });
      }
      return formatMessage(detail.history.moved, { from, to });
  }
};

/** The small second line: the file of an upload, the reason of a repair. */
export const historyDetail = (entry: RmHistoryEntry): string | undefined => {
  if (entry.meta?.type === 'magazine_uploaded') {
    const name = metaString(entry, 'name');
    const size = entry.meta?.size;
    if (!name) return undefined;
    return formatMessage(detail.history.fileLine, {
      name,
      size: formatFileSize(typeof size === 'number' ? size : undefined),
    });
  }
  const reason = metaString(entry, 'reason');
  return reason ? formatMessage(detail.history.reason, { reason }) : undefined;
};
